import { useState, useEffect } from 'react';
import { apiKeyApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/Table';
import {
  Key,
  User,
  Clock,
} from 'lucide-react';

interface APIKey {
  id: string;
  userId: string;
  username: string;
  name: string;
  keyPreview: string;
  createdAt: string;
  lastUsedAt?: string;
  status: 'active' | 'inactive';
}

export function APIKeysPage() {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  const fetchAPIKeys = async () => {
    setIsLoading(true);
    try {
      const response = await apiKeyApi.listAPIKeys({
        page,
        pageSize,
      });
      if (response.code === 0) {
        setApiKeys(response.data.items as APIKey[]);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAPIKeys();
  }, [page, pageSize]);

  const formatDate = (date?: string) => {
    if (!date) return '从未使用';
    return new Date(date).toLocaleDateString('zh-CN');
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success">正常</Badge>;
      case 'inactive':
        return <Badge variant="error">已禁用</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-zinc-900">API Keys</h1>
        <p className="text-zinc-500 mt-1">管理用户 API 密钥</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">总 API Keys</p>
                <p className="text-2xl font-bold text-zinc-900 mt-1">{total}</p>
              </div>
              <div className="p-3 bg-accent-100 rounded-xl">
                <Key className="w-6 h-6 text-accent-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">活跃 Keys</p>
                <p className="text-2xl font-bold text-zinc-900 mt-1">
                  {apiKeys.filter(k => k.status === 'active').length}
                </p>
              </div>
              <div className="p-3 bg-success-100 rounded-xl">
                <Key className="w-6 h-6 text-success-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">今日活跃</p>
                <p className="text-2xl font-bold text-zinc-900 mt-1">
                  {apiKeys.filter(k => k.lastUsedAt && new Date(k.lastUsedAt).toDateString() === new Date().toDateString()).length}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* API Keys Table */}
      <Card>
        <CardHeader>
          <CardTitle>API Key 列表</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>名称</TableHead>
                    <TableHead>用户</TableHead>
                    <TableHead>Key</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>创建时间</TableHead>
                    <TableHead>最后使用</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apiKeys.map((key) => (
                    <TableRow key={key.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Key className="w-4 h-4 text-zinc-400" />
                          <span className="font-medium text-zinc-900">{key.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-zinc-400" />
                          <span>{key.username}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="bg-zinc-100 px-2 py-1 rounded text-sm font-mono">
                          {key.keyPreview}
                        </code>
                      </TableCell>
                      <TableCell>{getStatusBadge(key.status)}</TableCell>
                      <TableCell>{formatDate(key.createdAt)}</TableCell>
                      <TableCell>
                        <span className={key.lastUsedAt ? 'text-zinc-900' : 'text-zinc-400'}>
                          {formatDate(key.lastUsedAt)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={page}
                totalPages={Math.ceil(total / pageSize)}
                onPageChange={setPage}
                totalItems={total}
                pageSize={pageSize}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
